import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Modal } from 'react-bootstrap';
import { useAuth } from './AuthContext';
import '../css/CardSettings.css';
import { getCurrentUser, deleteUser } from "aws-amplify/auth";
import { fetchUserProfileFromDB, saveUserProfileToDB } from "./dynamoUtils";
import { FaSignOutAlt, FaCog } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { deleteUserFromDB } from "./dynamoUtils";
import AvatarEditor from './AvatarEditor';
import { DEFAULT_AVATAR, resolveAvatarUrl } from "./avatar";

function CardSettings({
  onUserCreated,
}) {
  const { user } = useAuth();
  const { signOut } = useAuth();
  const [youtube, setYoutube] = useState(user.youtube || "");
  const [instagram, setInstagram] = useState(user.instagram || "");
  const [tiktok, setTiktok] = useState(user.tiktok || "");
  const [twitter, setTwitter] = useState(user.twitter || "");
  const [facebook, setFacebook] = useState(user.facebook || "");
  const [communityUpdateFrequency, setCommunityUpdateFrequency] = useState("Weekly"); // 👈 ADD THIS LINE
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  // eslint-disable-next-line
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(DEFAULT_AVATAR);
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleSignOut = async (signOutFunction) => {
    setShowSignOutModal(false);
    await signOutFunction();
    setModalMessage("Successfully signed out.");
    setShowModal(true);
  };

  const confirmSignOut = () => {
    setShowSignOutModal(true);
  };

  const handleDeleteAccount = async () => {
    try {
      setIsDeleting(true);

      // 1) Grab username while still signed in
      const current = await getCurrentUser(); // { username, userId }

      // 2) Delete app data first
      await deleteUserFromDB(current.username);

      // 3) Show success message *before* Cognito sign-out unmounts the page
      setModalMessage("Your account has been deleted successfully. We're sorry to see you go 💔");
      setShowModal(true);

      // 4) After a short delay, delete Cognito user (this will sign out + unmount)
      setTimeout(async () => {
        try {
          await deleteUser();
        } finally {
          navigate("/", { replace: true });
        }
      }, 2000); // 2s is enough to read the message
    } catch (error) {
      console.error("Error deleting account:", error);
      setModalMessage("❌ Error deleting account. Please try again later.");
      setShowModal(true);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleConfirmDelete = async () => {
    setShowConfirmModal(false);
    await handleDeleteAccount();
  };

  const confirmDeleteAccount = () => {
    setShowConfirmModal(true);
  };


  useEffect(() => {
    // Wait until the user object exists
    if (!user?.username) {
      // while waiting, keep showing default avatar
      setAvatarUrl(DEFAULT_AVATAR);
      return;
    }

    const fetchUserProfile = async () => {
      try {
        const userProfile = await fetchUserProfileFromDB(user.username);

        if (userProfile) {
          setAvatarUrl(resolveAvatarUrl(userProfile.avatar));
          setYoutube(userProfile.youtube || "");
          setInstagram(userProfile.instagram || "");
          setTiktok(userProfile.tiktok || "");
          setTwitter(userProfile.twitter || "");
          setFacebook(userProfile.facebook || "");
          setCommunityUpdateFrequency(
            userProfile.communityUpdateFrequency || "Weekly"
          );
        } else {
          // No profile found → fallback to default avatar
          setAvatarUrl(DEFAULT_AVATAR);
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
        setAvatarUrl(DEFAULT_AVATAR);
      }
    };

    fetchUserProfile();
  }, [user]); // 👈 depend on the full user object


  const handleFrequencyChangeAndSave = async (newFrequency) => {
    // 1. Update the UI immediately for a responsive feel
    setCommunityUpdateFrequency(newFrequency);

    // 2. Prepare a minimal object with ONLY the change to save
    const updatePayload = {
      communityUpdateFrequency: newFrequency,
    };

    try {
      // 3. Save only this one change to the database
      await saveUserProfileToDB(user.username, updatePayload);
      console.log(`✅ Auto-saved frequency: ${newFrequency}`); // Optional: for debugging
    } catch (error) {
      console.error("❌ Failed to auto-save frequency:", error);
      // You could add a user-facing error message here if desired
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();

    try {
      const updatedUserProfile = {
        youtube: youtube || "",  // Save social handles
        instagram: instagram || "",
        tiktok: tiktok || "",
        twitter: twitter || "",
        facebook: facebook || "",
        communityUpdateFrequency: communityUpdateFrequency,
        avatar: avatarUrl, // ✅ Save the cropped avatar
      };

      // ✅ Save updated profile to DynamoDB
      await saveUserProfileToDB(user.username, updatedUserProfile); // ✅ Use username

      setModalMessage("Profile updated successfully!");
      setShowModal(true);
    } catch (error) {
      console.error("Error saving profile:", error);
      setModalMessage("Error updating profile. Please try again.");
      setShowModal(true);
    }
  };


  // In CardSettings.jsx, replace everything from `return (` down to `</Container>);`

  return (
    <Container className="settings-card-container">
      <div className="settings-card-header">
        <FaCog className="settings-header-icon" />
        <span>Settings</span>
      </div>
      <div className="settings-card-content">
        <Form onSubmit={handleSave}>
          <div className="settings-header-wrapper">
            <div className="settings-header-top">
              <div className="settings-back-link" onClick={() => navigate('/progress')}>
                <FaSignOutAlt className="settings-back-icon" />
                <span>Upload Progress</span>
              </div>
            </div>
            <div className="settings-username-display">
              {user?.displayName || (user?.username && `@${user.username}`)}
              {user?.email && (
                <div className="settings-email-display">{user.email}</div>
              )}
            </div>
          </div>

          {/* User Info & Avatar Section */}
          <div className="settings-user-info-avatar">
            <div className="settings-section-title">Update Avatar</div>
            <AvatarEditor
              username={user.username}
              initialAvatarUrl={resolveAvatarUrl(avatarUrl)}
              onAvatarChange={(newUrl) => setAvatarUrl(resolveAvatarUrl(newUrl))}
            />
          </div>

          {/* Community Updates Section */}
          <div className="settings-section-title settings-section-title-community">Community Updates</div>
          <div className="update-options-container">
            {['None', 'Weekly'].map(option => (
              <div
                key={option}
                className={`update-option ${communityUpdateFrequency === option ? 'selected' : ''}`}
                onClick={() => handleFrequencyChangeAndSave(option)} // 👈 CHANGE THIS LINE
              >
                <div className="custom-checkbox">
                  <span className="checkmark">✓</span>
                </div>
                <span className="option-label">{option}</span>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="settings-button-group">
            <Button className="settings-btn-save" type="submit">
              Save
            </Button>
            <div className="settings-auth-buttons">
              <Button className="settings-btn-text" onClick={confirmSignOut}>
                Logout
              </Button>
              <Button className="settings-btn-text settings-btn-delete" onClick={confirmDeleteAccount}>
                Delete
              </Button>
            </div>
          </div>
        </Form>
      </div>

      {/* Modals for avatar editing, notifications, etc. (Keep these exactly as they are) */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Notification</Modal.Title>
        </Modal.Header>
        <Modal.Body>{modalMessage}</Modal.Body>
        <Modal.Footer>
          {modalMessage.includes("deleted") ? (
            <Button
              onClick={() => navigate("/", { replace: true })}
              style={{
                backgroundColor: "var(--color-black)",
                color: "var(--color-white)",
                borderRadius: "var(--radius-pill)",
                border: "0.5px solid var(--color-white)",
                padding: "10px 20px",
                fontSize: "12px",
                fontWeight: "normal",
                minWidth: "120px",
                transition: "background-color 0.3s ease, color 0.3s ease",
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = "var(--color-white)";
                e.target.style.color = "var(--color-black)";
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = "var(--color-black)";
                e.target.style.color = "var(--color-white)";
              }}
            >
              Return to Home
            </Button>
          ) : (
            <>
              <Button
                onClick={() => {
                  setShowModal(false);
                  navigate('/progress');
                }}
                style={{
                  backgroundColor: "var(--color-black)",
                  color: "var(--color-white)",
                  borderRadius: "var(--radius-pill)",
                  border: "0.5px solid var(--color-white)",
                  padding: "10px 20px",
                  fontSize: "12px",
                  fontWeight: "normal",
                  minWidth: "120px",
                  transition: "background-color 0.3s ease, color 0.3s ease",
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = "var(--color-white)";
                  e.target.style.color = "var(--color-black)";
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = "var(--color-black)";
                  e.target.style.color = "var(--color-white)";
                }}
              >
                Go to Progress
              </Button>

              <Button
                variant="secondary"
                onClick={() => setShowModal(false)}
              >
                Stay Here
              </Button>
            </>
          )}
        </Modal.Footer>

      </Modal>
      <Modal show={showSignOutModal} onHide={() => setShowSignOutModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Sign Out</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to sign out?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSignOutModal(false)}>
            No, Keep me Signed-in
          </Button>
          <Button variant="danger" onClick={() => handleSignOut(signOut)}>
            Yes, Sign me out
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete your account? All your progress will be lost.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete} disabled={isDeleting}>
            {isDeleting ? "Deleting..." : "Delete Account"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );

}

export default CardSettings;
